// ─── Expense Conflict View ───────────────────────────────────────────────────
//
// Amber accent to match ENTITY_TYPE_CONFIG.EXPENSE.tone = 'warning'.
// Traceability: FR-SYNC-013 (conflict resolution), FR-SYNC-015.

import { GLASS_FORM, cn } from '../../../constants/design-tokens';
import { EXPENSE_SCHEMA, formatValue, formatPaiseDecimal as formatPaise } from '@bbpos/utils';
import type { CurrencyCode } from '@bbpos/types';
import { GroupCard } from './GroupCard';
import { ConflictHeader } from './ConflictHeader';

export interface ExpenseConflictViewProps {
  clientVersion: Record<string, unknown>;
  serverVersion: Record<string, unknown>;
  deviceId: string | null;
  conflictMessage?: string;
  /**
   * Tenant currency for the amount field/header. Defaults to `'INR'`
   * (byte-identical); non-INR threads the correct symbol/subunit/decimals.
   */
  currency?: CurrencyCode;
}

export function ExpenseConflictView({
  clientVersion,
  serverVersion,
  deviceId,
  conflictMessage,
  currency = 'INR',
}: ExpenseConflictViewProps) {
  const description =
    (clientVersion.description as string | undefined) ??
    (serverVersion.description as string | undefined) ??
    'Untitled Expense';

  const amount =
    (clientVersion.amount as number | undefined) ??
    (serverVersion.amount as number | undefined);

  const statusRaw =
    (clientVersion.status as string | undefined) ??
    (serverVersion.status as string | undefined);

  const receiptUrl =
    (clientVersion.receipt_url as string | undefined) ??
    (serverVersion.receipt_url as string | undefined);

  return (
    <div className="space-y-4 animate-fade-in" data-testid="expense-conflict-view">
      <ConflictHeader
        entityLabel="Expense Conflict"
        identifier={description.length > 60 ? `${description.slice(0, 60)}\u2026` : description}
        accent={{
          from: 'from-amber-500/10',
          to: 'to-orange-500/10',
          ring: 'ring-amber-500/20',
          text: 'text-amber-700',
          darkText: 'dark:text-amber-300',
        }}
        tone="warning"
        badgeVariant="warning"
        badgeLabel={statusRaw ? formatValue('enum', statusRaw) : undefined}
        subtitle={amount != null ? (
          <p className="mt-0.5 text-lg font-bold font-mono tabular-nums text-amber-700 dark:text-amber-300">
            {formatPaise(amount, currency)}
          </p>
        ) : undefined}
        deviceId={deviceId}
        conflictMessage={conflictMessage}
        ariaLabel="Expense conflict header"
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {EXPENSE_SCHEMA.groups.map((group, i) => (
          <GroupCard
            key={group.title}
            title={group.title}
            fields={group.fields}
            clientVersion={clientVersion}
            serverVersion={serverVersion}
            currency={currency}
            delay={i * 60}
          />
        ))}
      </div>

      {receiptUrl && (
        <section className="space-y-2" role="region" aria-label="Expense receipt preview">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
            Receipt
          </h4>
          <div className={cn(GLASS_FORM, 'p-4')}>
            <a
              href={receiptUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-medium text-brand-600 dark:text-brand-400 hover:underline break-all"
            >
              {receiptUrl}
            </a>
          </div>
        </section>
      )}
    </div>
  );
}
